import { motion } from "framer-motion";
import { Phone, Mail, BadgeCheck } from "lucide-react";
import { useSiteContent, assetUrl, phoneHref } from "@/lib/siteContent";

export default function Owners() {
  const { owners: entries, headings } = useSiteContent();
  const owners = entries.map((owner) => ({ ...owner, testid: owner.id }));
  return (
    <section id="owners" data-testid="owners-section" className="py-24 md:py-32 bg-navy border-y border-slate-800/60">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p className="text-electric font-semibold tracking-[0.3em] text-sm uppercase mb-4" data-testid="owners-eyebrow">{headings.owners.eyebrow}</p>
          <h2 className="font-bebas text-4xl md:text-6xl tracking-tight chrome-text">
            {headings.owners.title}
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl font-light">
            {headings.owners.description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {owners.map((o, i) => (
            <motion.div
              key={o.name}
              data-testid={`owner-card-${o.testid}`}
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
              whileHover={{ y: -8 }}
              className="metal-card rounded-3xl p-6 sm:p-10 relative overflow-hidden group min-w-0"
            >
              <div
                className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10 blur-2xl group-hover:opacity-25 transition-opacity duration-500"
                style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 70%)" }}
                aria-hidden="true"
              />
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 shrink-0 overflow-hidden rounded-full bg-gradient-to-br from-slate-300 via-slate-500 to-slate-700 flex items-center justify-center">
                  {o.image ? <img data-testid={`owner-photo-${o.id}`} src={assetUrl(o.image)} alt={o.name} className="w-full h-full object-cover" /> : <span className="font-bebas text-3xl text-navy-deep">{o.name[0]}</span>}
                </div>
                <div>
                  <h3 className="font-bebas text-3xl tracking-wide text-white">{o.name}</h3>
                  <p className="flex items-center gap-1.5 text-electric text-xs font-semibold tracking-[0.2em] uppercase">
                    <BadgeCheck className="w-4 h-4 shrink-0" /> {o.role}
                  </p>
                </div>
              </div>

              <a
                href={phoneHref(o.phone)}
                data-testid={`call-button-${o.testid}`}
                className="mt-8 flex items-center justify-center gap-3 w-full bg-electric text-navy-deep rounded-2xl py-5 hover:scale-[1.02] transition-transform duration-300 glow-blue"
              >
                <Phone className="w-6 h-6" />
                <span className="font-bebas text-3xl tracking-wider">{o.phone}</span>
              </a>

              {o.email && (
                <a
                  href={`mailto:${o.email}`}
                  data-testid={`email-link-${o.testid}`}
                  className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-electric transition-colors duration-300"
                >
                  <Mail className="w-4 h-4 shrink-0" /> <span className="break-all">{o.email}</span>
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
